import { Link } from "react-router-dom";

export interface ArticleLike {
  slug: string;
  title: string;
  summary: string;
  category: string;
  categorySlug: string;
  tags: string[];
  author: string;
  date: string;
  readTime: number;
  views: number;
  cover: string;
  coverAlt: string;
}

interface ArticleCardProps {
  article: ArticleLike;
  variant?: "default" | "wide" | "compact";
  index?: number;
}

export default function ArticleCard({ article, variant = "default", index }: ArticleCardProps) {
  if (variant === "compact") {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex cursor-pointer items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 hover:bg-primary-50"
      >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-100 font-heading text-sm font-bold text-accent-800">
          {typeof index === "number" ? String(index + 1).padStart(2, "0") : "·"}
        </span>
        <span className="flex flex-col">
          <span className="font-heading text-sm font-semibold leading-6 text-foreground-900 transition-colors group-hover:text-primary-700 md:text-base">
            {article.title}
          </span>
          <span className="mt-2 flex flex-wrap items-center gap-3 text-xs text-foreground-500">
            <span className="text-primary-600">{article.category}</span>
            <span>{article.date}</span>
            <span>{article.readTime} 分钟</span>
          </span>
        </span>
      </Link>
    );
  }

  if (variant === "wide") {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group grid cursor-pointer grid-cols-1 overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300 md:grid-cols-12"
      >
        <div className="h-56 w-full md:col-span-5 md:h-full">
          <img
            src={article.cover}
            alt={article.coverAlt}
            className="h-full w-full object-cover object-top"
          />
        </div>
        <div className="flex flex-col justify-center p-5 md:col-span-7 md:p-8">
          <span className="flex w-fit items-center gap-2 rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800">
            <i className="ri-bookmark-3-line"></i>
            {article.category}
          </span>
          <h3 className="mt-4 font-heading text-lg font-bold leading-8 text-foreground-950 transition-colors group-hover:text-primary-700 md:text-2xl">
            {article.title}
          </h3>
          <p className="mt-3 text-sm leading-7 text-foreground-600 line-clamp-2">{article.summary}</p>
          <div className="mt-5 flex flex-wrap items-center gap-4 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-user-3-line"></i>
              {article.author}
            </span>
            <span>{article.date}</span>
            <span>{article.readTime} 分钟阅读</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group flex cursor-pointer flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
    >
      <div className="h-48 w-full">
        <img
          src={article.cover}
          alt={article.coverAlt}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="flex flex-1 flex-col p-5">
        <span className="text-xs font-medium tracking-wide text-primary-600">{article.category}</span>
        <h3 className="mt-2 font-heading text-base font-bold leading-7 text-foreground-950 transition-colors group-hover:text-primary-700 md:text-lg">
          {article.title}
        </h3>
        <p className="mt-3 flex-1 text-sm leading-7 text-foreground-600 line-clamp-3">{article.summary}</p>
        <div className="mt-5 flex items-center justify-between border-t border-background-200 pt-4 text-xs text-foreground-500">
          <span>{article.date}</span>
          <span className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.views.toLocaleString()}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {article.readTime} 分钟
            </span>
          </span>
        </div>
      </div>
    </Link>
  );
}